import React, { useEffect, useState, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Box, Paper, Typography } from "@mui/material";
import { useReport } from "./utils/useReport";
import { useNavigate, useParams } from "react-router-dom";
import ConditioningForm from "./components/conditioningForm";
import dayjs from "dayjs";
import { IFormNewReport, ServiceReport, ControlReportResult } from "@app/types/report.types";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { CreatePageList } from "@app/components/Layouts/PageList";
import { Loading, MButton, CustomDataGrid } from "@app/components";
import { getDefaultValuesReport } from "./utils/helpers";
import MessageDialog from "@app/components/Login/MessageDialog";
import { errorAlert } from "@app/utils/alerts";
import { TypeReport } from "./utils/constants";
import { columnsControlReport } from "./utils/controlReport.columns";

interface EditReportProps {
  mode: MODES_OPTIONS;
}

interface InitialStateConfirm {
  open: boolean;
  message: string;
  title: string;
}

const inititalStateConfirm: InitialStateConfirm = {
  open: false,
  message: "",
  title: "Guardar los cambios",
};

const EditReport = ({ mode }: EditReportProps) => {
  const { idReport } = useParams();
  const navigate = useNavigate();
  const isView = mode === MODES_OPTIONS.VIEW;
  const isEdit = mode === MODES_OPTIONS.EDIT;
  const isCopy = mode === MODES_OPTIONS.COPY;

  const [openConfirmModal, setOpenConfirmModal] =
    useState<InitialStateConfirm>(inititalStateConfirm);
  const [dataToSave, setDataToSave] = useState<IFormNewReport | null>(null);

  const [reportPreload, setReportPreload] = useState<
    ServiceReport | undefined
  >();
  const [controlReportResults, setControlReportResults] = useState<ControlReportResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  
  const {
    save,
    get,
    loadingSave,
    downloadReport,
    downloadControlReport,
    downloadLIDReport,
    loadingDownload,
    fetchControlReportFiltered,
    loadingControlReport,
    downloadControlReportWithFilters,
  } = useReport();

  const form = useForm<IFormNewReport>({
    mode: "all",
    defaultValues: getDefaultValuesReport(reportPreload),
  });
  const reportType = form.getValues("reportType");

  const hasFetched = useRef(false);

  useEffect(() => {
    const fetchReport = async () => {
     if (!idReport || hasFetched.current) return; 
      hasFetched.current = true; 

      const report = await get(Number(idReport));

      if (!report) {
        errorAlert("Informe no encontrado");
        navigate("/admin/informes");
        return;
      }

      const reportNotEditable =
        (!isView && report.original_report === report.id) ||
        (isEdit && report.fecha_entrega !== null);

      if (reportNotEditable) {
        errorAlert("El informe no puede ser editado");
        navigate("/admin/informes");
        return;
      }

      setReportPreload(report);
    };

    fetchReport();
  }, [idReport, isEdit]);

  useEffect(() => {
    if (reportPreload) {
      const defaultValues = getDefaultValuesReport(reportPreload);
      form.reset(defaultValues);
    }
  }, [reportPreload]);

  const submit = async (data: IFormNewReport) => {
    const newData = isCopy
      ? { ...data, id: undefined, original_report: idReport }
      : data;

    const status = await save(newData);
    if (status) {
      navigate(-1);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSearch = async () => {
    // Primero guardar el informe silenciosamente
    const formData = form.getValues();
    const dataToSaveNow = { ...formData, id: Number(idReport) };
    
    const saved = await save(dataToSaveNow, true); // true para indicar que no muestre alerta
    
    if (!saved) {
      errorAlert("Error al guardar el informe antes de buscar resultados");
      return;
    }
    
    // Luego obtener los resultados con los filtros aplicados
    const id = Number(idReport);
    const filters = {
      activity_comparator: formData.activity_comparator,
      activity_value: formData.activity_value,
      activity_value_max: formData.activity_value_max,
      uncertainty_comparator: formData.uncertainty_comparator,
      uncertainty_value: formData.uncertainty_value,
      uncertainty_value_max: formData.uncertainty_value_max,
      lid_comparator: formData.lid_comparator,
      lid_value: formData.lid_value,
      lid_value_max: formData.lid_value_max,
    };
    
    const results = await fetchControlReportFiltered(id, filters);
    
    if (results) {
      setControlReportResults(results);
      setShowResults(true);
    }
  };

  const handleView = () => {
    const id = Number(idReport);
    const refReport = reportPreload?.ref_report || "";
    const resultsConsecutive = reportPreload?.results_consecutive || 0;
    const fechaEntrega = reportPreload?.fecha_entrega || "";

    if (reportType === TypeReport.REPORT_RESULT) {
      downloadReport(id, refReport, resultsConsecutive, fechaEntrega); 
    }

    if (reportType === TypeReport.REPORT_CONTROL) {
      downloadControlReport(id, refReport);
    }

    if (reportType === TypeReport.REPORT_LID) {
      downloadLIDReport(id, refReport); 
    }
  };

  const handleSave = (data: IFormNewReport) => {
    if (isEdit) {
      let title = "¿Está seguro de que desea guardar los cambios?";
      if (reportPreload?.report_lid) {
        if (reportPreload?.fecha_entrega) {
          title = `El informe ${reportPreload?.ref_report} ya ha sido enviado.Se generará un nuevo informe a partir del anterior.`;
        }
      }
      const dataSaved = { ...data, id: Number(idReport) };
      setDataToSave(dataSaved);
      setOpenConfirmModal({
        open: true,
        message: "¿Desea continuar?",
        title,
      });
    } else submit(data);
  };

  const handleDialogClose = () => setOpenConfirmModal(inititalStateConfirm);

  const handleDialogConfirm = () => {
    if (dataToSave) {
      submit(dataToSave);
    }
    handleDialogClose();
  };

  const getTitle = () => {
    const refReport = reportPreload?.ref_report || "";
    const consecutive = reportPreload?.results_consecutive
      ? `/${reportPreload.results_consecutive}`
      : "";

    if (isView) {
      return `Ver informe ${refReport}${consecutive}`;
    }

    if (isEdit) {
      return `Editar informe ${refReport}${consecutive}`;
    }

    return "Nuevo informe";
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        <CreatePageList
          title={getTitle()}
          footer={
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              {!isView ? (
                <MButton
                  variant="contained"
                  color="primary"
                  text="Guardar"
                  type="submit"
                />
              ) : (
                <MButton
                  variant="contained"
                  color="primary"
                  sx={{ ml: 2 }}
                  text="Visualizar"
                  onClick={handleView}
                  disabled={loadingDownload}
                />
              )}
              <MButton
                variant="outlined"
                color="secondary"
                sx={{ ml: 2 }}
                text="Cancelar"
                onClick={handleBack}
              />
            </Box>
          }
        >
          <ConditioningForm mode={mode} />
          
          {reportType === TypeReport.REPORT_CONTROL && !isView && idReport && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <MButton
                variant="contained"
                color="secondary"
                text="Buscar Resultados"
                onClick={handleSearch}
                disabled={loadingControlReport}
              />
            </Box>
          )}
          
          {reportType === TypeReport.REPORT_CONTROL && showResults && controlReportResults.length > 0 && (
            <Paper sx={{ mt: 4, p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Resultados de Control
              </Typography>
              <CustomDataGrid
                rows={controlReportResults.map((row, index) => ({ id: index, ...row }))}
                columns={columnsControlReport}
                autoHeight
                disableRowSelectionOnClick
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 },
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
              />
            </Paper>
          )}
        </CreatePageList>
      </form>
      {(loadingSave || loadingDownload || loadingControlReport) && <Loading />}
      <MessageDialog
        data={{
          component: "Confirm",
          title: openConfirmModal.title,
          message: openConfirmModal.message,
          status: "confirm",
          active: openConfirmModal.open,
          handleConfirm: handleDialogConfirm,
        }}
        handleClose={handleDialogClose}
        handleConfirm={handleDialogConfirm}
      />
    </FormProvider>
  );
};

export default EditReport;
